// Register.tsx — public sign-up page on react-bootstrap.
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import { BsPlusLg } from 'react-icons/bs'
import Text from 'common/ui/Text'
import { GitHubIcon, GoogleIcon } from './icons'

const Register = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const returnTo = searchParams.get('return_to') || ''

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (password !== confirmPassword) {
            setError('Passwords do not match')
            return
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters')
            return
        }

        setLoading(true)
        try {
            const res = await fetch('/api/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
                credentials: 'include',
            })

            if (!res.ok) {
                const data = await res.json()
                throw new Error(data.error || 'Registration failed')
            }

            const data = await res.json()
            localStorage.setItem('identity_token', data.token)

            if (returnTo) {
                window.location.href = returnTo
            } else {
                navigate('/dashboard')
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'An unexpected error occurred')
        } finally {
            setLoading(false)
        }
    }

    const handleSocialLogin = (provider: string) => {
        const redirectUrl = returnTo
            ? `/api/auth/${provider}?return_to=${encodeURIComponent(returnTo)}`
            : `/api/auth/${provider}`
        window.location.href = redirectUrl
    }

    return (
        <div className="auth-wrapper">
            <Card style={{ width: '420px', maxWidth: '100%' }}>
                <Card.Body>
                    <h2 className="text-center mb-4">Sign Up</h2>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <div className="d-flex flex-column gap-2 mb-3">
                        <Button
                            variant="github"
                            onClick={() => handleSocialLogin('github')}
                            className="d-flex align-items-center justify-content-center gap-2"
                        >
                            <GitHubIcon /> Sign up with GitHub
                        </Button>
                        <Button
                            variant="google"
                            onClick={() => handleSocialLogin('google')}
                            className="d-flex align-items-center justify-content-center gap-2"
                        >
                            <GoogleIcon /> Sign up with Google
                        </Button>
                    </div>

                    <div className="text-center text-muted my-3">OR</div>

                    <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
                        <Form.Group controlId="register-email">
                            <Form.Label>Email</Form.Label>
                            <Text
                                type="email"
                                style={{ width: "100%" }}
                                required
                                value={email}
                                placeholder="Enter email"
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                            />
                        </Form.Group>
                        <Form.Group controlId="register-password">
                            <Form.Label>Password</Form.Label>
                            <Text
                                type="password"
                                style={{ width: "100%" }}
                                required
                                value={password}
                                placeholder="At least 8 characters"
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                            />
                        </Form.Group>
                        <Form.Group controlId="register-confirm">
                            <Form.Label>Confirm password</Form.Label>
                            <Text
                                type="password"
                                style={{ width: "100%" }}
                                required
                                value={confirmPassword}
                                placeholder="Re-enter your password"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                        </Form.Group>
                        <Button variant="success" type="submit" disabled={loading} className="d-flex align-items-center justify-content-center gap-2">
                            <BsPlusLg /> {loading ? 'Creating…' : 'Create Account'}
                        </Button>
                    </Form>

                    <div className="text-center mt-3 text-muted">
                        <span>Already have an account? </span>
                        <Button
                            variant="link"
                            className="p-0 align-baseline"
                            onClick={() => navigate('/login' + (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''))}
                        >
                            Log In
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default Register
